/**
 * WebRTC Call State Machine
 *
 * Manages predictable state transitions for video call connection lifecycle.
 * Single source of truth for call state - no more scattered boolean flags!
 */

import { PlatformInfo } from '../platform';

/**
 * Error types that can occur during a call
 */
export interface CallError {
  code: 'PERMISSION_DENIED' | 'NO_DEVICES' | 'CONNECTION_FAILED' | 'TIMEOUT' | 'UNKNOWN';
  message: string;
  details?: any;
}

/**
 * Call state - discriminated union for type safety
 */
export type CallState =
  | { status: 'idle' }
  | { status: 'requesting-permissions' }
  | { status: 'initializing'; platform: PlatformInfo }
  | { status: 'waiting-for-peer'; role: 'host' | 'guest' }
  | {
      status: 'negotiating';
      isPolite: boolean;
      makingOffer: boolean;
    }
  | {
      status: 'offer-collision';
      resolutionStrategy: 'rollback' | 'ignore';
    }
  | {
      status: 'connecting';
      progress: 'offer-sent' | 'answer-received' | 'ice-gathering' | 'ice-connected';
    }
  | { status: 'connected'; connectedAt: number; peerSocketId?: string }
  | { status: 'reconnecting'; attempt: number; reason: string; maxAttempts: number }
  | { status: 'host-left'; countdown: number; startedAt: number }
  | { status: 'failed'; error: CallError; canRetry: boolean }
  | { status: 'ended'; reason: 'user' | 'host' | 'timeout' | 'error'; endedAt: number };

/**
 * Events that can trigger state transitions
 */
export type CallEvent =
  | { type: 'START_CALL'; platform: PlatformInfo; role: 'host' | 'guest' }
  | { type: 'PERMISSIONS_REQUESTED' }
  | { type: 'PERMISSIONS_GRANTED'; stream: MediaStream }
  | { type: 'PERMISSIONS_DENIED'; error: CallError }
  | { type: 'PEER_JOINED'; socketId: string }
  | { type: 'NEGOTIATION_NEEDED' }
  | { type: 'OFFER_COLLISION_DETECTED'; isPolite: boolean }
  | { type: 'ROLLBACK_COMPLETED' }
  | { type: 'OFFER_SENT' }
  | { type: 'OFFER_RECEIVED'; offer: RTCSessionDescription }
  | { type: 'ANSWER_SENT' }
  | { type: 'ANSWER_RECEIVED'; answer: RTCSessionDescription }
  | { type: 'ICE_GATHERING' }
  | { type: 'ICE_CONNECTED' }
  | { type: 'CONNECTED' }
  | { type: 'CONNECTION_FAILED'; error: CallError }
  | { type: 'PEER_LEFT' }
  | { type: 'HOST_LEFT' }
  | { type: 'RECONNECT'; attempt: number }
  | { type: 'COUNTDOWN_TICK'; remaining: number }
  | { type: 'COUNTDOWN_EXPIRED' }
  | { type: 'END_CALL'; reason: 'user' | 'host' | 'timeout' | 'error' }
  | { type: 'RETRY' };

/**
 * State transition function - pure function for predictability
 */
function transition(currentState: CallState, event: CallEvent): CallState {
  switch (currentState.status) {
    case 'idle':
      if (event.type === 'START_CALL') {
        return {
          status: 'waiting-for-peer',
          role: event.role,
        };
      }
      // ✅ FIX: Handle PEER_JOINED in idle state (guest waiting for host scenario)
      if (event.type === 'PEER_JOINED') {
        console.log('[CallStateMachine] Peer joined while idle - staying idle (will transition after startCall)');
        return currentState; // Stay in idle, let startCall transition to waiting-for-peer
      }
      // ✅ FIX: Allow offer/answer events from idle state (race condition when guest joins before host calls startCall)
      if (event.type === 'OFFER_RECEIVED') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      if (event.type === 'ANSWER_SENT') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      // ✅ FIX: Handle PEER_LEFT in idle state (guest-joins-first scenario)
      if (event.type === 'PEER_LEFT') {
        console.log('[CallStateMachine] Peer left while idle - staying idle');
        return currentState;
      }
      break;

    case 'requesting-permissions':
      if (event.type === 'PERMISSIONS_REQUESTED') {
        return currentState; // Stay in same state
      }
      if (event.type === 'PERMISSIONS_GRANTED') {
        return {
          status: 'initializing',
          platform: event.stream ? currentState : currentState,
        } as any; // Will be set properly
      }
      if (event.type === 'PERMISSIONS_DENIED') {
        return {
          status: 'failed',
          error: event.error,
          canRetry: true,
        };
      }
      // Allow START_CALL to transition directly to waiting-for-peer if permissions already granted
      if (event.type === 'START_CALL') {
        return {
          status: 'waiting-for-peer',
          role: event.role,
        };
      }
      break;

    case 'initializing':
      if (event.type === 'PEER_JOINED') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      break;

    case 'waiting-for-peer':
      if (event.type === 'PEER_JOINED') {
        // Stay in waiting-for-peer - let OFFER_SENT transition us to connecting
        return currentState;
      }
      if (event.type === 'OFFER_SENT') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      if (event.type === 'OFFER_RECEIVED') {
        // Received offer while waiting (guest-joins-first scenario)
        return {
          status: 'connecting',
          progress: 'answer-received',
        };
      }
      if (event.type === 'ANSWER_SENT') {
        // Sent answer after receiving offer (guest-joins-first scenario)
        return {
          status: 'connecting',
          progress: 'answer-received',
        };
      }
      if (event.type === 'NEGOTIATION_NEEDED') {
        return {
          status: 'negotiating',
          isPolite: currentState.role === 'host',
          makingOffer: true,
        };
      }
      if (event.type === 'END_CALL') {
        return {
          status: 'ended',
          reason: event.reason,
          endedAt: Date.now(),
        };
      }
      break;

    case 'negotiating':
      if (event.type === 'OFFER_COLLISION_DETECTED') {
        return {
          status: 'offer-collision',
          resolutionStrategy: event.isPolite ? 'rollback' : 'ignore',
        };
      }
      if (event.type === 'OFFER_SENT') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      if (event.type === 'ANSWER_SENT') {
        return {
          status: 'connecting',
          progress: 'answer-received',
        };
      }
      break;

    case 'offer-collision':
      if (event.type === 'ROLLBACK_COMPLETED') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      if (event.type === 'ANSWER_SENT') {
        return {
          status: 'connecting',
          progress: 'answer-received',
        };
      }
      break;

    case 'connecting':
      if (event.type === 'PEER_JOINED') {
        // Already connecting, ignore duplicate peer-joined events
        return currentState;
      }
      if (event.type === 'OFFER_SENT') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      if (event.type === 'ANSWER_RECEIVED') {
        return {
          status: 'connecting',
          progress: 'answer-received',
        };
      }
      if (event.type === 'ICE_GATHERING') {
        return {
          status: 'connecting',
          progress: 'ice-gathering',
        };
      }
      if (event.type === 'ICE_CONNECTED') {
        return {
          status: 'connecting',
          progress: 'ice-connected',
        };
      }
      if (event.type === 'CONNECTED') {
        return {
          status: 'connected',
          connectedAt: Date.now(),
        };
      }
      if (event.type === 'CONNECTION_FAILED') {
        return {
          status: 'failed',
          error: event.error,
          canRetry: true,
        };
      }
      break;

    case 'connected':
      if (event.type === 'PEER_LEFT') {
        return {
          status: 'reconnecting',
          attempt: 1,
          reason: 'peer-disconnected',
          maxAttempts: 3,
        };
      }
      if (event.type === 'HOST_LEFT') {
        return {
          status: 'host-left',
          countdown: 60,
          startedAt: Date.now(),
        };
      }
      if (event.type === 'CONNECTION_FAILED') {
        return {
          status: 'reconnecting',
          attempt: 1,
          reason: 'connection-lost',
          maxAttempts: 3,
        };
      }
      if (event.type === 'END_CALL') {
        return {
          status: 'ended',
          reason: event.reason,
          endedAt: Date.now(),
        };
      }
      break;

    case 'reconnecting':
      if (event.type === 'CONNECTED') {
        return {
          status: 'connected',
          connectedAt: Date.now(),
        };
      }
      if (event.type === 'RECONNECT') {
        if (event.attempt >= currentState.maxAttempts) {
          return {
            status: 'failed',
            error: {
              code: 'CONNECTION_FAILED',
              message: 'Maximum reconnection attempts exceeded',
            },
            canRetry: false,
          };
        }
        return {
          status: 'reconnecting',
          attempt: event.attempt,
          reason: currentState.reason,
          maxAttempts: currentState.maxAttempts,
        };
      }
      if (event.type === 'END_CALL') {
        return {
          status: 'ended',
          reason: event.reason,
          endedAt: Date.now(),
        };
      }
      break;

    case 'host-left':
      if (event.type === 'COUNTDOWN_TICK') {
        return {
          status: 'host-left',
          countdown: event.remaining,
          startedAt: currentState.startedAt,
        };
      }
      if (event.type === 'COUNTDOWN_EXPIRED') {
        return {
          status: 'ended',
          reason: 'timeout',
          endedAt: Date.now(),
        };
      }
      if (event.type === 'END_CALL') {
        return {
          status: 'ended',
          reason: event.reason,
          endedAt: Date.now(),
        };
      }
      // Host rejoins during countdown
      if (event.type === 'PEER_JOINED') {
        return {
          status: 'connecting',
          progress: 'offer-sent',
        };
      }
      break;

    case 'failed':
      if (event.type === 'RETRY' && currentState.canRetry) {
        return { status: 'idle' };
      }
      if (event.type === 'END_CALL') {
        return {
          status: 'ended',
          reason: 'error',
          endedAt: Date.now(),
        };
      }
      break;

    case 'ended':
      // Terminal state - no transitions
      break;
  }

  // Invalid transition - return current state
  console.warn(`Invalid transition: ${currentState.status} + ${event.type}`);
  return currentState;
}

/**
 * Checks if a transition is valid without executing it
 */
function canTransition(currentState: CallState, event: CallEvent): boolean {
  const nextState = transition(currentState, event);
  return nextState !== currentState;
}

/**
 * State machine class with history tracking
 */
export class CallStateMachine {
  private state: CallState;
  private history: Array<{ state: CallState; timestamp: number }> = [];
  private listeners: Array<(state: CallState, previousState: CallState) => void> = [];

  constructor(initialState: CallState = { status: 'idle' }) {
    this.state = initialState;
    this.history.push({ state: initialState, timestamp: Date.now() });
  }

  /**
   * Gets the current state
   */
  getState(): CallState {
    return this.state;
  }

  /**
   * Executes a state transition
   */
  transition(event: CallEvent): CallState {
    const previousState = this.state;
    const nextState = transition(this.state, event);

    if (nextState !== previousState) {
      this.state = nextState;
      this.history.push({ state: nextState, timestamp: Date.now() });

      // Log transition for debugging
      console.log(
        `[CallStateMachine] ${previousState.status} → ${nextState.status}`,
        { event, nextState }
      );

      // Notify listeners
      this.listeners.forEach((listener) => listener(nextState, previousState));
    }

    return this.state;
  }

  /**
   * Checks if a transition is valid
   */
  canTransition(event: CallEvent): boolean {
    return canTransition(this.state, event);
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: CallState, previousState: CallState) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Gets the state history
   */
  getHistory(): Array<{ state: CallState; timestamp: number }> {
    return this.history;
  }

  /**
   * Resets the state machine
   */
  reset(): void {
    this.state = { status: 'idle' };
    this.history = [{ state: this.state, timestamp: Date.now() }];
  }

  /**
   * Helper: Check if in a specific status
   */
  is(status: CallState['status']): boolean {
    return this.state.status === status;
  }

  /**
   * Helper: Check if in any of the given statuses
   */
  isAnyOf(...statuses: CallState['status'][]): boolean {
    return statuses.includes(this.state.status);
  }

  /**
   * Helper: Check if connected
   */
  isConnected(): boolean {
    return this.state.status === 'connected';
  }

  /**
   * Helper: Check if in a terminal state
   */
  isTerminal(): boolean {
    return this.state.status === 'ended' || this.state.status === 'failed';
  }

  /**
   * Helper: Check if can retry
   */
  canRetry(): boolean {
    return this.state.status === 'failed' && this.state.canRetry;
  }
}
